import { useEffect, useId, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { BookOpenText, Plus, Trash2 } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { EmptyState } from '@/components/ui/empty-state';
import { Input, Textarea } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Spinner } from '@/components/ui/spinner';
import { useToast } from '@/components/ui/use-toast';
import {
  useProject,
  useProjectGlossary,
  useUpsertGlossary,
  type GlossaryTerm,
} from '@/lib/api';

interface DraftTerm extends GlossaryTerm {
  /** Stable local id so React can key rows that haven't been persisted yet. */
  _localId: string;
}

let nextLocalId = 1;
function makeId() {
  return `local-${++nextLocalId}`;
}

export function GlossaryTab() {
  const { projId = '' } = useParams<{ projId: string }>();
  const project = useProject(projId);
  const glossary = useProjectGlossary(projId);
  const upsert = useUpsertGlossary();
  const toast = useToast();

  const targets = useMemo(
    () => project.data?.targetLanguageCodes ?? [],
    [project.data?.targetLanguageCodes],
  );
  const [styleGuide, setStyleGuide] = useState('');
  const [terms, setTerms] = useState<DraftTerm[]>([]);
  const styleId = useId();

  useEffect(() => {
    if (glossary.data) {
      setStyleGuide(glossary.data.styleGuide ?? '');
      setTerms((glossary.data.terms ?? []).map((t) => ({ ...t, _localId: makeId() })));
    }
  }, [glossary.data]);

  if (project.isLoading || glossary.isLoading) {
    return <Spinner label="Loading glossary…" />;
  }
  if (!project.data) return null;

  function addRow() {
    setTerms((cur) => [
      ...cur,
      {
        _localId: makeId(),
        source: '',
        caseSensitive: false,
        doNotTranslate: false,
        translations: {},
      },
    ]);
  }

  function updateRow(id: string, patch: Partial<DraftTerm>) {
    setTerms((cur) => cur.map((t) => (t._localId === id ? { ...t, ...patch } : t)));
  }

  function setTranslation(id: string, lang: string, value: string) {
    setTerms((cur) =>
      cur.map((t) => {
        if (t._localId !== id) return t;
        const next: Record<string, string> = { ...(t.translations ?? {}) };
        if (value) next[lang] = value;
        else delete next[lang];
        return { ...t, translations: next };
      }),
    );
  }

  function removeRow(id: string) {
    setTerms((cur) => cur.filter((t) => t._localId !== id));
  }

  async function save() {
    const cleaned = terms
      .filter((t) => (t.source ?? '').trim() !== '')
      .map(({ _localId: _l, ...rest }) => rest);
    try {
      await upsert.mutateAsync({ projectID: projId, styleGuide, terms: cleaned });
      toast.show('Glossary saved', 'success');
    } catch (err) {
      toast.show((err as Error).message, 'error');
    }
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="inline-flex items-center gap-2">
            <BookOpenText className="size-4 text-primary" aria-hidden />
            Style guide
          </CardTitle>
          <CardDescription>
            Markdown notes injected into the AI translator's system prompt. Use it for tone,
            register, formatting rules, brand voice.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-2">
          <Label htmlFor={styleId}>Notes</Label>
          <Textarea
            id={styleId}
            value={styleGuide}
            onChange={(e) => setStyleGuide(e.target.value)}
            rows={8}
            placeholder="# Tone&#10;Use the formal &quot;vous&quot; form in French; avoid contractions."
            className="font-mono text-xs"
          />
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-start justify-between gap-3 space-y-0">
          <div>
            <CardTitle>Glossary</CardTitle>
            <CardDescription>
              Term-by-term translations and do-not-translate markers. Surfaced as chips in the
              editor and used by the AI translator to keep brand terms consistent.
            </CardDescription>
          </div>
          <Button variant="outline" size="sm" onClick={addRow}>
            <Plus aria-hidden />
            Add term
          </Button>
        </CardHeader>
        <CardContent className="space-y-3">
          {terms.length === 0 ? (
            <EmptyState
              icon={BookOpenText}
              title="No glossary terms yet"
              description="Add product names, technical terms, and brand vocabulary you want the AI to keep consistent."
              action={
                <Button variant="outline" size="sm" onClick={addRow}>
                  <Plus aria-hidden />
                  Add the first term
                </Button>
              }
            />
          ) : (
            <ul className="space-y-3">
              {terms.map((t) => (
                <li
                  key={t._localId}
                  className="space-y-3 rounded-md border border-border bg-surface p-3"
                >
                  <div className="grid gap-3 lg:grid-cols-[1fr_2fr_auto]">
                    <div className="space-y-1">
                      <Label className="text-xs">Source term</Label>
                      <Input
                        value={t.source ?? ''}
                        onChange={(e) => updateRow(t._localId, { source: e.target.value })}
                        placeholder="Fluentix"
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Notes</Label>
                      <Input
                        value={t.description ?? ''}
                        onChange={(e) => updateRow(t._localId, { description: e.target.value })}
                        placeholder="Why translators should care"
                      />
                    </div>
                    <div className="flex items-end gap-2">
                      <label className="flex items-center gap-1 text-[11px] text-muted-foreground">
                        <input
                          type="checkbox"
                          checked={!!t.caseSensitive}
                          onChange={(e) =>
                            updateRow(t._localId, { caseSensitive: e.target.checked })
                          }
                        />
                        Case-sensitive
                      </label>
                      <label className="flex items-center gap-1 text-[11px] text-muted-foreground">
                        <input
                          type="checkbox"
                          checked={!!t.doNotTranslate}
                          onChange={(e) =>
                            updateRow(t._localId, { doNotTranslate: e.target.checked })
                          }
                        />
                        Do not translate
                      </label>
                      <Button
                        variant="ghost"
                        size="icon"
                        aria-label="Remove term"
                        onClick={() => removeRow(t._localId)}
                      >
                        <Trash2 className="size-4" aria-hidden />
                      </Button>
                    </div>
                  </div>
                  {!t.doNotTranslate && targets.length > 0 && (
                    <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
                      {targets.map((lang) => (
                        <div key={lang} className="space-y-1">
                          <Label className="text-[10px] uppercase tracking-wide text-muted-foreground">
                            <Badge tone="outline" className="mr-1">
                              {lang}
                            </Badge>
                          </Label>
                          <Input
                            value={t.translations?.[lang] ?? ''}
                            onChange={(e) => setTranslation(t._localId, lang, e.target.value)}
                            placeholder={`${t.source || 'term'} → ${lang}`}
                          />
                        </div>
                      ))}
                    </div>
                  )}
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      <div className="flex items-center justify-end">
        <Button onClick={() => void save()} disabled={upsert.isPending}>
          {upsert.isPending ? 'Saving…' : 'Save glossary'}
        </Button>
      </div>
    </div>
  );
}
